/**
 * Composable for handling authentication logic
 */
import type { PasswordVerificationResponse, AuthToken } from '~/types';

export function useAuth() {
  /**
   * Verifies a password by sending it to the server API
   */
  const verifyPassword = async (password: string): Promise<PasswordVerificationResponse> => {
    if (!process.client) {
      console.warn('Password verification should only be called client-side');
      return { success: false, message: 'Password verification unavailable' };
    }

    try {
      const { apiBase } = useRuntimeConfig().public;
      const response = await fetch(`${apiBase}/verify-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error in password verification:', error);
      return {
        success: false,
        message: 'Error processing request'
      };
    }
  };

  /**
   * Creates an authentication token and stores it in localStorage
   */
  const createAuthToken = () => {
    if (!process.client) return;

    // Create a token
    const token: AuthToken = {
      authenticated: true,
      // Set expiration to 24 hours from now
      expires: Date.now() + 1000 * 60 * 60 * 24,
      token: `auth_${Math.random().toString(36).substring(2, 15)}_${Date.now()}`
    };

    // Store in localStorage
    localStorage.setItem('auth_token', JSON.stringify(token));

    return token;
  };

  /**
   * Checks if the user is authenticated by verifying stored token
   */
  const checkAuthentication = (): boolean => {
    if (!process.client) return false;

    try {
      // Get token from localStorage
      const tokenJson = localStorage.getItem('auth_token');
      if (!tokenJson) return false;

      const token = JSON.parse(tokenJson) as AuthToken;

      // Check if token is expired
      if (token.expires < Date.now()) {
        // Token expired, clean up
        localStorage.removeItem('auth_token');
        return false;
      }

      // Valid token
      return token.authenticated === true;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  };

  /**
   * Logs the user out by removing the auth token
   */
  const logout = () => {
    if (!process.client) return;
    localStorage.removeItem('auth_token');
  };

  return {
    verifyPassword,
    createAuthToken,
    checkAuthentication,
    logout
  };
} 